import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

// ─── Bi no Kisu Palette (inherits TopStep command center) ────────────
const C = {
  gold:    '#D4AF37',
  green:   '#66BB6A',
  red:     '#EF5350',
  orange:  '#FFA726',
  white:   'rgba(255,255,255,0.85)',
  muted:   'rgba(255,255,255,0.40)',
  surface: '#161B27',
  border:  'rgba(255,255,255,0.07)',
};

// ─── Data Interfaces ─────────────────────────────────────────────────
export interface FibLadderPosition {
  symbol: string;
  size: number;
  pnl: number;
  hwm: number;
  effectiveStop: number;
  entryPrice: number;
  side: 'long' | 'short';
}

interface Props {
  positions: FibLadderPosition[];
  takeProfitTarget?: number;
  stopLossFloor?: number;
  accountLabel?: string;
}

// ─── Tier Definitions ────────────────────────────────────────────────
const TIERS = [
  { label: 'Golden Trail',  y0: 400, y1: 600, fill: 'rgba(212,175,55,0.07)',  fib: '1.000' },
  { label: 'Locked Profit', y0: 200, y1: 400, fill: 'rgba(102,187,106,0.05)', fib: '0.618' },
  { label: 'Breakeven+',    y0: 150, y1: 200, fill: 'rgba(255,255,255,0.03)', fib: '0.382' },
  { label: 'Recovery',      y0:   0, y1: 150, fill: 'rgba(255,167,38,0.04)',  fib: '0.236' },
  { label: 'Risk Zone',     y0:-200, y1:   0, fill: 'rgba(239,83,80,0.06)',   fib: '' },
];

// ─── Fibonacci Spiral Path ──────────────────────────────────────────
// Quarter-arc segments from Fibonacci sequence scaled to fit bottom-right
function buildSpiralPath(cx: number, cy: number, scale: number): string {
  const fibs = [1, 1, 2, 3, 5, 8, 13, 21];
  const segments: string[] = [];
  let x = cx, y = cy;
  for (let i = 0; i < fibs.length; i++) {
    const r = fibs[i] * scale;
    const dir = i % 4;
    let ex: number, ey: number;
    let sweep = 1;
    switch (dir) {
      case 0: ex = x + r; ey = y - r; break;       // right-up
      case 1: ex = x + r; ey = y + r; break;        // right-down
      case 2: ex = x - r; ey = y + r; break;        // left-down
      default: ex = x - r; ey = y - r; sweep = 1;   // left-up
    }
    segments.push(`A ${r} ${r} 0 0 ${sweep} ${ex.toFixed(1)} ${ey.toFixed(1)}`);
    x = ex; y = ey;
  }
  return `M ${cx} ${cy} ` + segments.join(' ');
}

// ─── Helper: Tier Color for Position ────────────────────────────────
function positionColor(pnl: number): string {
  if (pnl >= 400) return C.gold;
  if (pnl >= 200) return C.green;
  if (pnl >= 150) return C.white;
  if (pnl >= 0)   return C.orange;
  return C.red;
}

function stopColor(effectiveStop: number): string {
  if (effectiveStop >= 100) return C.green;
  if (effectiveStop >= 25)  return C.white;
  if (effectiveStop >= 0)   return 'rgba(255,255,255,0.30)';
  return C.red;
}

// ─── Component ──────────────────────────────────────────────────────
export default function FibonacciLadderViz({
  positions,
  takeProfitTarget = 600,
  stopLossFloor = -200,
  accountLabel,
}: Props) {
  const W = 700;
  const H = 400;
  const PAD_L = 56;   // left margin for Y-axis labels
  const PAD_R = 90;   // right margin for tier labels
  const PAD_T = 20;
  const PAD_B = 36;   // bottom margin for symbol labels
  const chartW = W - PAD_L - PAD_R;
  const chartH = H - PAD_T - PAD_B;
  const range = takeProfitTarget - stopLossFloor; // 800

  // Map P&L value to Y coordinate (inverted: higher P&L = higher on screen = lower Y)
  const toY = (pnl: number) => {
    const clamped = Math.max(stopLossFloor, Math.min(takeProfitTarget, pnl));
    return PAD_T + chartH - ((clamped - stopLossFloor) / range) * chartH;
  };

  // X positions: evenly spaced across chart width, max 7 instruments
  const posCount = Math.max(positions.length, 1);
  const toX = (i: number) => PAD_L + (chartW / (posCount + 1)) * (i + 1);

  // P&L tier boundary lines
  const tierLines = [
    { pnl: takeProfitTarget, label: `+$${takeProfitTarget}`, style: 'tp' },
    { pnl: 400, label: '+$400', style: 'tier' },
    { pnl: 300, label: '+$300', style: 'tier' },
    { pnl: 200, label: '+$200', style: 'tier' },
    { pnl: 150, label: '+$150', style: 'minor' },
    { pnl: 0,   label: '$0',    style: 'zero' },
    { pnl: stopLossFloor, label: `$${stopLossFloor}`, style: 'floor' },
  ];

  const spiralPath = buildSpiralPath(W - PAD_R - 40, H - PAD_B - 30, 2.5);

  return (
    <Box sx={{ width: '100%', overflow: 'hidden' }}>
      {accountLabel && (
        <Typography
          variant="overline"
          sx={{ color: C.gold, fontSize: '0.7rem', letterSpacing: 1.5, display: 'block', mb: 0.5 }}
        >
          Fibonacci Recovery Ladder {accountLabel && `\u2014 ${accountLabel}`}
        </Typography>
      )}

      <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} preserveAspectRatio="xMidYMid meet">
        <defs>
          {/* Glow filter for position markers */}
          <filter id="fibGlow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur in="SourceGraphic" stdDeviation="3" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        <style>{`
          .fib-dot { animation: fibPulse 3s ease-in-out infinite; }
          .fib-dot:nth-child(2n) { animation-delay: 0.5s; }
          .fib-dot:nth-child(3n) { animation-delay: 1s; }
          @keyframes fibPulse {
            0%, 100% { opacity: 0.8; }
            50% { opacity: 1; }
          }
          .fib-marker { transition: cy 1.5s cubic-bezier(0.4, 0, 0.2, 1), cx 0.5s ease; }
          .fib-stop-line { transition: y1 1.5s cubic-bezier(0.4, 0, 0.2, 1), y2 1.5s cubic-bezier(0.4, 0, 0.2, 1); }
          .fib-hwm-line { transition: y1 1.5s ease, y2 1.5s ease; }
        `}</style>

        {/* ── Golden Spiral Background ── */}
        <path
          d={spiralPath}
          fill="none"
          stroke="rgba(212,175,55,0.06)"
          strokeWidth="1.5"
          strokeLinecap="round"
        />

        {/* ── Tier Zone Fills ── */}
        {TIERS.map((tier, i) => {
          const y1 = toY(tier.y1);
          const y2 = toY(tier.y0);
          return (
            <rect
              key={i}
              x={PAD_L}
              y={y1}
              width={chartW}
              height={y2 - y1}
              fill={tier.fill}
            />
          );
        })}

        {/* ── Tier Boundary Lines + Labels ── */}
        {tierLines.map((line, i) => {
          const y = toY(line.pnl);
          const isZero = line.style === 'zero';
          const isFloor = line.style === 'floor';
          const isTp = line.style === 'tp';
          return (
            <g key={i}>
              <line
                x1={PAD_L} y1={y} x2={PAD_L + chartW} y2={y}
                stroke={
                  isZero ? 'rgba(255,255,255,0.25)' :
                  isFloor ? 'rgba(239,83,80,0.35)' :
                  isTp ? 'rgba(212,175,55,0.35)' :
                  'rgba(255,255,255,0.08)'
                }
                strokeWidth={isZero || isFloor ? 1.2 : 0.7}
                strokeDasharray={isZero ? '6 3' : isFloor || isTp ? '4 2' : '2 3'}
              />
              {/* Left Y-axis label */}
              <text
                x={PAD_L - 6}
                y={y + 3}
                textAnchor="end"
                fill={
                  isZero ? 'rgba(255,255,255,0.50)' :
                  isFloor ? C.red :
                  isTp ? C.gold :
                  C.muted
                }
                fontSize="11"
                fontFamily="Inter, sans-serif"
                fontWeight={isZero || isFloor || isTp ? 600 : 400}
              >
                {line.label}
              </text>
            </g>
          );
        })}

        {/* ── Tier Labels (right margin) ── */}
        {TIERS.map((tier, i) => {
          const midY = toY((tier.y0 + tier.y1) / 2) + 3;
          return (
            <g key={`label-${i}`}>
              <text
                x={PAD_L + chartW + 8}
                y={midY}
                fill={C.muted}
                fontSize="10"
                fontFamily="Inter, sans-serif"
                fontWeight={500}
                letterSpacing="0.5"
              >
                {tier.label}
              </text>
              {tier.fib && (
                <text
                  x={PAD_L + chartW + 8}
                  y={midY + 14}
                  fill={C.gold}
                  fontSize="9"
                  fontFamily="Inter, sans-serif"
                  fontWeight={700}
                  opacity={0.6}
                >
                  {tier.fib}
                </text>
              )}
            </g>
          );
        })}

        {/* ── Position Markers ── */}
        {positions.map((pos, i) => {
          const x = toX(i);
          const y = toY(pos.pnl);
          const stopY = toY(pos.effectiveStop);
          const hwmY = toY(pos.hwm);
          const r = 5 + Math.min(pos.size, 12) * 0.55;
          const color = positionColor(pos.pnl);
          const sColor = stopColor(pos.effectiveStop);
          const hasHwmGap = pos.hwm > pos.pnl + 10;

          return (
            <g key={pos.symbol}>
              {/* HWM ghost trail — shows retrace from peak */}
              {hasHwmGap && (
                <line
                  className="fib-hwm-line"
                  x1={x} y1={y} x2={x} y2={hwmY}
                  stroke="rgba(212,175,55,0.15)"
                  strokeWidth="1"
                  strokeDasharray="2 2"
                />
              )}
              {hasHwmGap && (
                <circle
                  cx={x} cy={hwmY} r="2"
                  fill="none"
                  stroke="rgba(212,175,55,0.25)"
                  strokeWidth="0.7"
                />
              )}

              {/* Trailing stop line */}
              <line
                className="fib-stop-line"
                x1={x} y1={y} x2={x} y2={stopY}
                stroke={sColor}
                strokeWidth="1.8"
                strokeDasharray="4 2"
                opacity={0.5}
              />
              {/* Stop level tick mark */}
              <line
                x1={x - 6} y1={stopY} x2={x + 6} y2={stopY}
                stroke={sColor}
                strokeWidth="1.8"
                opacity={0.5}
              />

              {/* Outer glow ring */}
              <circle
                className="fib-dot"
                cx={x}
                cy={y}
                r={r + 5}
                fill="none"
                stroke={color}
                strokeWidth="0.7"
                opacity={0.3}
                filter="url(#fibGlow)"
              />

              {/* Position dot */}
              <circle
                className="fib-marker fib-dot"
                cx={x}
                cy={y}
                r={r}
                fill={color}
                opacity={0.9}
                filter="url(#fibGlow)"
              />

              {/* P&L value label */}
              <text
                x={x}
                y={y - r - 7}
                textAnchor="middle"
                fill={color}
                fontSize="12"
                fontFamily="Inter, sans-serif"
                fontWeight={700}
              >
                {pos.pnl >= 0 ? '+' : ''}{`$${pos.pnl.toFixed(0)}`}
              </text>

              {/* Symbol label (bottom) */}
              <text
                x={x}
                y={H - 8}
                textAnchor="middle"
                fill={C.white}
                fontSize="12"
                fontFamily="Inter, sans-serif"
                fontWeight={600}
                letterSpacing="0.5"
              >
                {pos.symbol}
              </text>

              {/* Size indicator */}
              <text
                x={x}
                y={H - 22}
                textAnchor="middle"
                fill={C.muted}
                fontSize="9"
                fontFamily="Inter, sans-serif"
                fontWeight={400}
              >
                {pos.size}x
              </text>
            </g>
          );
        })}

        {/* ── Empty State ── */}
        {positions.length === 0 && (
          <text
            x={W / 2}
            y={H / 2}
            textAnchor="middle"
            fill={C.muted}
            fontSize="14"
            fontFamily="Inter, sans-serif"
          >
            No open positions
          </text>
        )}
      </svg>
    </Box>
  );
}
